import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, today } from "@/lib/api";
import { PURCHASE_PAYMENT_TYPES, COST_CATEGORIES, PAYMENT_TYPES, SALE_PAYMENT_TYPES, carLabel } from "@/lib/labels";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

export function PurchaseDialog({ open, onOpenChange, cars, fixedCar, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({
        car_id: fixedCar?.id || "", payment_type: "cash", price: "", dp_amount: "",
        paid_off: false, date: today(), seller_name: "", notes: "",
        tax_due_date: "", tax_amount: "",
        n_merk: "", n_model: "", n_year: String(new Date().getFullYear()), n_color: "", n_plate: "", n_km: "",
        photos: [],
        n_branch_id: user?.role === "admin_cabang" ? user.branch_id : "",
        cash_branch_id: user?.role === "admin_cabang" ? user.branch_id : (fixedCar?.branch_id || ""),
      });
      api.get("/branches").then((r) => {
        const visible = user?.role === "admin_cabang" ? r.data.filter((b) => b.id === user.branch_id || b.is_pusat) : r.data;
        setBranches(visible);
        if (visible.length) setForm((f) => (f && !f.n_branch_id ? { ...f, n_branch_id: visible[0].id } : f));
      });
    }
  }, [open, fixedCar, user]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        dp_amount: form.payment_type === "dp" ? parseFloat(form.dp_amount) || 0 : 0,
        paid_off: form.payment_type === "cash" ? true : !!form.paid_off,
        tax_due_date: form.tax_due_date || null,
        tax_amount: parseFloat(form.tax_amount) || null,
      };
      delete payload.photos;
      if (form.car_id === "__new__") {
        payload.car_id = null;
        payload.new_car = { merk: form.n_merk, model: form.n_model, year: parseInt(form.n_year) || 0, color: form.n_color, plate: form.n_plate, km: parseInt(form.n_km) || 0, branch_id: form.n_branch_id };
      }
      const res = await api.post("/purchases", payload);
      if (form.photos.length && res.data.car_id) {
        let failed = 0;
        for (const f of form.photos) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/cars/${res.data.car_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch {
            failed += 1;
          }
        }
        if (failed) toast.error(`${failed} foto gagal diunggah — bisa ditambahkan lewat halaman detail mobil`);
      }
      toast.success("Pembelian berhasil dicatat");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Catat Pembelian</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="purchase-form">
          {!fixedCar && (
            <div className="space-y-2">
              <Label className={labelCls}>Mobil</Label>
              <Select value={form.car_id} onValueChange={(v) => setForm((f) => ({ ...f, car_id: v, cash_branch_id: v === "__new__" ? f.n_branch_id : (cars.find((c) => c.id === v)?.branch_id || f.cash_branch_id) }))}>
                <SelectTrigger data-testid="purchase-car-select" className={inputCls}><SelectValue placeholder="Pilih mobil" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {cars.map((c) => <SelectItem key={c.id} value={c.id}>{carLabel(c)}</SelectItem>)}
                  <SelectItem value="__new__" className="text-blue-500 font-medium">+ Mobil Baru (langsung jadi stok)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {!fixedCar && form.car_id === "__new__" && (
            <div className="space-y-3 p-3 border border-blue-500/20 bg-blue-500/5 rounded-md" data-testid="purchase-new-car-fields">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-blue-500">Data Mobil Baru</p>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="purchase-nc-merk" required value={form.n_merk} onChange={(e) => set("n_merk", e.target.value)} placeholder="Merk (Daihatsu)" className={inputCls} />
                <Input data-testid="purchase-nc-model" required value={form.n_model} onChange={(e) => set("n_model", e.target.value)} placeholder="Jenis (Terios X AT)" className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input data-testid="purchase-nc-year" type="number" required value={form.n_year} onChange={(e) => set("n_year", e.target.value)} placeholder="Tahun" className={inputCls} />
                <Input data-testid="purchase-nc-color" value={form.n_color} onChange={(e) => set("n_color", e.target.value)} placeholder="Warna" className={inputCls} />
                <Input data-testid="purchase-nc-plate" value={form.n_plate} onChange={(e) => set("n_plate", e.target.value)} placeholder="Plat" className={inputCls} />
              </div>
              <Input data-testid="purchase-nc-km" type="number" min="0" value={form.n_km} onChange={(e) => set("n_km", e.target.value)} placeholder="KM (odometer)" className={inputCls} />
              <div className="space-y-2">
                <Label className={labelCls}>Cabang Mobil</Label>
                <Select value={form.n_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, n_branch_id: v, cash_branch_id: v }))}>
                  <SelectTrigger data-testid="purchase-nc-branch" className={inputCls}><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {form.car_id && (
            <div className="space-y-2">
              <Label className={labelCls}>Foto Mobil · Opsional</Label>
              <input
                data-testid="purchase-photos-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = [...(e.target.files || [])].slice(0, Math.max(0, 6 - form.photos.length));
                  if (files.length) set("photos", [...form.photos, ...files]);
                  e.target.value = "";
                }}
                className="block w-full text-xs text-zinc-400 file:mr-3 file:h-9 file:px-3 file:rounded-md file:border-0 file:bg-[#121218] file:text-zinc-200 file:text-xs file:font-semibold hover:file:bg-zinc-700 file:cursor-pointer"
              />
              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="purchase-photos-preview">
                  {form.photos.map((f, i) => (
                    <div key={`${f.name}-${f.size}-${f.lastModified}`} className="relative aspect-video bg-[#121218] rounded-sm overflow-hidden">
                      <img src={URL.createObjectURL(f)} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                      <button type="button" data-testid={`purchase-photo-remove-${i}`} onClick={() => set("photos", form.photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#050505]/80 text-zinc-300 flex items-center justify-center hover:text-rose-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-zinc-500">Maks. 6 foto, masing-masing hingga 5MB. Foto diunggah setelah pembelian tersimpan.</p>
            </div>
          )}
          <div className="pt-2 border-t border-[#1A1A22]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Data Pembelian</p>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Sumber Dana (Buku Kas)</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => set("cash_branch_id", v)}>
              <SelectTrigger data-testid="purchase-cash-branch-select" className={inputCls}><SelectValue placeholder="Ikut cabang mobil" /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                {user?.role !== "admin_cabang" && <SelectItem value="umum">Kas Umum Pusat (Tanpa Cabang)</SelectItem>}
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-500">Kas keluar pembelian dicatat di buku kas ini (bawaan: cabang mobil).</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Tipe Pembayaran</Label>
              <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
                <SelectTrigger data-testid="purchase-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {Object.entries(PURCHASE_PAYMENT_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="purchase-date-input" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Harga Beli (Rp)</Label>
            <Input data-testid="purchase-price-input" type="number" min="1" required value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="150000000" className={inputCls} />
          </div>
          {form.payment_type === "dp" && (
            <>
              <div className="space-y-2">
                <Label className={labelCls}>Jumlah DP (Rp)</Label>
                <Input data-testid="purchase-dp-input" type="number" min="1" required value={form.dp_amount} onChange={(e) => set("dp_amount", e.target.value)} placeholder="50000000" className={inputCls} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="paid_off"
                  data-testid="purchase-paidoff-checkbox"
                  checked={form.paid_off}
                  onCheckedChange={(v) => set("paid_off", !!v)}
                />
                <Label htmlFor="paid_off" className="text-sm text-zinc-300">Sudah lunas</Label>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label className={labelCls}>Nama Penjual / Sumber</Label>
            <Input data-testid="purchase-seller-input" value={form.seller_name} onChange={(e) => set("seller_name", e.target.value)} placeholder="Nama pemilik sebelumnya" className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Pajak Berlaku s.d. (Bulan & Tahun)</Label>
            <Input data-testid="purchase-tax-date-input" type="month" value={form.tax_due_date} onChange={(e) => set("tax_due_date", e.target.value)} className={inputCls} />
            <p className="text-[11px] text-zinc-500">Status Hidup/Mati terisi otomatis dari bulan & tahun ini.</p>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Estimasi Biaya (Rp) · Opsional</Label>
            <Input data-testid="purchase-tax-amount-input" type="number" min="0" value={form.tax_amount} onChange={(e) => set("tax_amount", e.target.value)} placeholder="3500000" className={inputCls} />
            <p className="text-[11px] text-zinc-500">Estimasi biaya tambahan (perpanjangan pajak, balik nama, dll) — tampil di info stok untuk semua cabang.</p>
          </div>
          <Button data-testid="purchase-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pembelian"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CostDialog({ open, onOpenChange, cars, fixedCar, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({ car_id: fixedCar?.id || "", category: "body_repair", amount: "", date: today(), description: "", cash_branch_id: user?.role === "admin_cabang" ? user.branch_id : fixedCar?.branch_id || "" });
      api.get("/branches").then((r) => {
        const visible = user?.role === "admin_cabang" ? r.data.filter((b) => b.id === user.branch_id || b.is_pusat) : r.data;
        setBranches(visible);
        if (visible.length) {
          setForm((f) => (f && !f.cash_branch_id ? { ...f, cash_branch_id: visible[0].id } : f));
        }
      });
    }
  }, [open, fixedCar, user]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/costs", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Biaya berhasil dicatat");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Catat Biaya Mobil</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="cost-form">
          {!fixedCar && (
            <div className="space-y-2">
              <Label className={labelCls}>Mobil</Label>
              <Select value={form.car_id} onValueChange={(v) => setForm((f) => ({ ...f, car_id: v, cash_branch_id: user?.role === "admin_cabang" ? f.cash_branch_id : cars.find((c) => c.id === v)?.branch_id || f.cash_branch_id }))}>
                <SelectTrigger data-testid="cost-car-select" className={inputCls}><SelectValue placeholder="Pilih mobil" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {cars.map((c) => <SelectItem key={c.id} value={c.id}>{carLabel(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="cost-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {Object.entries(COST_CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="cost-date-input" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Sumber Dana (Kas Cabang / Pusat)</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => set("cash_branch_id", v)}>
              <SelectTrigger data-testid="cost-cash-branch-select" className={inputCls}><SelectValue placeholder="Pilih sumber dana" /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                {user?.role !== "admin_cabang" && <SelectItem value="umum">Kas Umum Pusat (Tanpa Cabang)</SelectItem>}
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Jumlah (Rp)</Label>
            <Input data-testid="cost-amount-input" type="number" min="1" required value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="5000000" className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Keterangan</Label>
            <Input data-testid="cost-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Catatan biaya" className={inputCls} />
          </div>
          <Button data-testid="cost-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Biaya"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SaleDialog({ open, onOpenChange, cars, fixedCar, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({
        car_id: fixedCar?.id || "", payment_type: "cash", price: "", dp_amount: "",
        leasing_name: "", buyer_name: "", buyer_phone: "", salesperson_name: "", commission: "", date: today(), notes: "",
        cash_branch_id: user?.role === "admin_cabang" ? user.branch_id : fixedCar?.branch_id || "",
      });
      api.get("/branches").then((r) => {
        const visible = user?.role === "admin_cabang" ? r.data.filter((b) => b.id === user.branch_id || b.is_pusat) : r.data;
        setBranches(visible);
        if (visible.length) setForm((f) => (f && !f.cash_branch_id ? { ...f, cash_branch_id: visible[0].id } : f));
      });
    }
  }, [open, fixedCar, user]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sales", {
        ...form,
        price: parseFloat(form.price) || 0,
        dp_amount: parseFloat(form.dp_amount) || 0,
        commission: parseFloat(form.commission) || 0,
      });
      toast.success("Penjualan berhasil dicatat");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Catat Penjualan</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="sale-form">
          {!fixedCar && (
            <div className="space-y-2">
              <Label className={labelCls}>Mobil</Label>
              <Select value={form.car_id} onValueChange={(v) => setForm((f) => ({ ...f, car_id: v, cash_branch_id: user?.role === "admin_cabang" ? f.cash_branch_id : cars.find((c) => c.id === v)?.branch_id || f.cash_branch_id }))}>
                <SelectTrigger data-testid="sale-car-select" className={inputCls}><SelectValue placeholder="Pilih mobil" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {cars.map((c) => <SelectItem key={c.id} value={c.id}>{carLabel(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Tipe Pembayaran</Label>
              <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
                <SelectTrigger data-testid="sale-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {Object.entries(SALE_PAYMENT_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="sale-date-input" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Harga Jual (Rp) · Opsional</Label>
            <Input data-testid="sale-price-input" type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Kosongkan jika belum pasti" className={inputCls} />
            <p className="text-[11px] text-zinc-500">Harga bisa ditentukan nanti; dana penjualan dapat diterima bertahap ke kas.</p>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Kas Penerima Dana</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => set("cash_branch_id", v)}>
              <SelectTrigger data-testid="sale-cash-branch-select" className={inputCls}><SelectValue placeholder="Pilih kas cabang / pusat" /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                <SelectItem value="umum">Kas Umum Pusat (Tanpa Cabang)</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>DP / Pembayaran Awal (Rp) · Opsional</Label>
            <Input data-testid="sale-dp-input" type="number" min="0" value={form.dp_amount} onChange={(e) => set("dp_amount", e.target.value)} placeholder="30000000" className={inputCls} />
            <p className="text-[11px] text-zinc-500">Pembayaran berikutnya dicatat lewat tombol Terima Dana hingga lunas.</p>
          </div>
          {form.payment_type === "leasing" && (
            <div className="space-y-2">
              <Label className={labelCls}>Nama Leasing</Label>
              <Input data-testid="sale-leasing-input" required value={form.leasing_name} onChange={(e) => set("leasing_name", e.target.value)} placeholder="BCA Finance, Mandiri Tunas, dll" className={inputCls} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Nama Pembeli</Label>
              <Input data-testid="sale-buyer-input" required value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} placeholder="Nama pembeli" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>No HP Pembeli</Label>
              <Input data-testid="sale-buyer-phone-input" value={form.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} placeholder="08xxxxxxxxxx" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Nama Penjual (Sales)</Label>
              <Input data-testid="sale-salesperson-input" value={form.salesperson_name} onChange={(e) => set("salesperson_name", e.target.value)} placeholder="Nama sales" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Komisi Penjual (Rp)</Label>
              <Input data-testid="sale-commission-input" type="number" min="0" value={form.commission} onChange={(e) => set("commission", e.target.value)} placeholder="1000000" className={inputCls} />
            </div>
          </div>
          <Button data-testid="sale-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Penjualan"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
